import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import { DateFormat } from "../../../modules/constants";
import SmartPageHeader from "../../../components/smart/PageHeader/PageHeader";
import DailyEntries from "../../../components/smart/LCF401/DailyEntries/DailyEntries";
import { DailyListKeys } from "../../../components/smart/LCF401/DailyEntries/Constants";
import {
  dailyEntries,
  mapDispatchToProps,
  mapStateToProps,
} from "../../../components/smart/LCF401/DailyEntries/DailyEntries.store";
import DietDiaryMenuItems from "../../../components/smart/LCF401/DietDiaryMenuItems/DietDiaryMenuItems";

const ParticipantLanding = (props) => {
  const {
    dayBeforeEntries,
    yesterdayEntries,
    todayEntries,
    fetchEntries,
    route,
    studies,
    canAddMeal,
    resetState,
    participantDateOfEnrollment,
  } = props;

  const { t } = useTranslation();

  const [todayDate, setTodayDate] = useState(new Date());
  const [isVisible, setIsVisible] = useState();

  const onVisibilityChange = () => {
    setIsVisible(!document.hidden);
  };

  useEffect(() => {
    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const refreshData = useCallback(() => {
    fetchEntries({ date: moment(), key: DailyListKeys.todayListName });
    fetchEntries({
      date: moment().subtract(1, "days"),
      key: DailyListKeys.yesterdayListName,
    });
    fetchEntries({
      date: moment().subtract(2, "days"),
      key: DailyListKeys.dayBeforeListName,
    });
  }, [fetchEntries]);

  useEffect(() => {
    if (isVisible && !moment(todayDate).isSame(moment(new Date()), "day")) {
      setTodayDate(new Date());
      refreshData();
    }
  }, [isVisible, refreshData, todayDate]);

  useEffect(() => {
    if (!todayEntries) {
      fetchEntries({ date: moment(), key: DailyListKeys.todayListName });
    }
  }, [todayEntries, fetchEntries]);

  useEffect(() => {
    if (!yesterdayEntries) {
      fetchEntries({
        date: moment().subtract(1, "days"),
        key: DailyListKeys.yesterdayListName,
      });
    }
  }, [yesterdayEntries, fetchEntries]);

  useEffect(() => {
    if (!dayBeforeEntries) {
      fetchEntries({
        date: moment().subtract(2, "days"),
        key: DailyListKeys.dayBeforeListName,
      });
    }
  }, [dayBeforeEntries, fetchEntries]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  return (
    <>
      <SmartPageHeader
        title={studies && studies[0]?.code}
        studyId={route.study}
        subtitle=""
        roleName="Participant"
        shouldFetchParticipant={true}
      />

      {canAddMeal && <DietDiaryMenuItems refreshData={refreshData} />}

      <DailyEntries
        title={t("appText.today")}
        date={moment()}
        totalsTitle={t("appText.dietDiary.totals.today.title")}
        entries={todayEntries}
        studyId={route.study}
        closeDietDiaryAction={() => refreshData()}
        participantDateOfEnrollment={participantDateOfEnrollment}
        refetchData={(date) =>
          fetchEntries({ date: date, key: DailyListKeys.todayListName })
        }
      />
      <DailyEntries
        title={t("appText.yesterday")}
        date={moment().subtract(1, "days")}
        totalsTitle={t("appText.dietDiary.totals.yesterday.title")}
        listTitle={t("contentText.dietDiary.entriesPanel.yesterday.title")}
        entries={yesterdayEntries}
        studyId={route.study}
        closeDietDiaryAction={() => refreshData()}
        participantDateOfEnrollment={participantDateOfEnrollment}
        refetchData={(date) =>
          fetchEntries({
            date: date,
            key: DailyListKeys.yesterdayListName,
          })
        }
      />
      <DailyEntries
        title={t("appText.dayBefore")}
        date={moment().subtract(2, "days")}
        totalsTitle={`${moment()
          .subtract(2, "days")
          .format(DateFormat.EXTENDED_FORMAT)} ${t("appText.total")}`}
        listTitle={`${moment()
          .subtract(2, "days")
          .format(DateFormat.EXTENDED_FORMAT)} ${t(
          "contentText.dietDiary.entriesTable.title"
        )}`}
        entries={dayBeforeEntries}
        studyId={route.study}
        closeDietDiaryAction={() => refreshData()}
        participantDateOfEnrollment={participantDateOfEnrollment}
        refetchData={(date) =>
          fetchEntries({
            date: date,
            key: DailyListKeys.dayBeforeListName,
          })
        }
      />
    </>
  );
};

const combineMapStateToProps = (state) => ({
  ...mapStateToProps(state),
  participantDateOfEnrollment:
    state.smartPageHeaderReducer?.participantDateOfEnrollment,
});

export default dynamic(
  "ParticipantLanding",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({
      loginReducer: state.loginReducer,
    }),
  }),
  attachReducer(dailyEntries)
)(connect(combineMapStateToProps, mapDispatchToProps)(ParticipantLanding));
